"use client";

import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";

import { Section, Typography } from "@/components/ui";
import { NewButton } from "@/components/ui/NewButton";
import { CDN_IMAGE_QUALITY, getCdnImageProps } from "@/lib/utils/cdn-image";

export interface UseCaseTabItem {
  image: string;
  imageAlt: string;
  imageClassName?: string;
  imageFit?: "cover" | "contain";
  tag?: string;
  tagColor?: string;
  title: string;
  description: React.ReactNode;
  valuePoint?: string;
  href?: string;
  linkText?: string;
}

interface UseCaseTabsSectionProps {
  id?: string;
  sectionLabel?: string;
  title: string;
  titleClassName?: string;
  subtitle?: React.ReactNode;
  items: UseCaseTabItem[];
  /** Optional call to action rendered below the panels. */
  cta?: {
    href: string;
    label: string;
    target?: string;
  };
  /** Always scroll tabs horizontally instead of wrapping on desktop. */
  scrollableTabs?: boolean;
  className?: string;
}

// Fixed header + subnav; tab strip height below that offset.
const STICKY_OFFSET = 110;
const MOBILE_SCROLL_OFFSET_FALLBACK = STICKY_OFFSET + 96;

export const UseCaseTabsSection: React.FC<UseCaseTabsSectionProps> = ({
  id,
  sectionLabel,
  title,
  titleClassName,
  subtitle,
  items,
  cta,
  scrollableTabs = false,
  className = "",
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [mobileScrollOffset, setMobileScrollOffset] = useState(MOBILE_SCROLL_OFFSET_FALLBACK);
  const panelRefs = useRef<(HTMLDivElement | null)[]>([]);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const stickyHeaderRef = useRef<HTMLDivElement | null>(null);
  const isClickScrolling = useRef(false);
  const isInitialMount = useRef(true);

  // Desktop shows only the active panel (no sticky bar, no scroll-spy).
  // Mobile keeps the sticky bar with scroll-to-section behavior.
  const isDesktop = () =>
    typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches;

  useEffect(() => {
    const measureStickyHeader = () => {
      if (isDesktop() || !stickyHeaderRef.current) return;
      setMobileScrollOffset(STICKY_OFFSET + stickyHeaderRef.current.offsetHeight);
    };

    measureStickyHeader();
    window.addEventListener("resize", measureStickyHeader);
    return () => window.removeEventListener("resize", measureStickyHeader);
  }, [title, sectionLabel, items.length, activeIndex]);

  useEffect(() => {
    const update = () => {
      if (isClickScrolling.current || isDesktop()) return;
      let active = 0;
      panelRefs.current.forEach((panel, index) => {
        if (!panel) return;
        if (panel.getBoundingClientRect().top <= mobileScrollOffset + 20) active = index;
      });
      setActiveIndex(prev => (prev === active ? prev : active));
    };

    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [mobileScrollOffset]);

  // Keep the active tab visible in the horizontal strip on mobile.
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (isDesktop()) return;
    tabRefs.current[activeIndex]?.scrollIntoView({
      block: "nearest",
      inline: "center",
      behavior: "smooth",
    });
  }, [activeIndex]);

  const goToPanel = (index: number) => {
    const panel = panelRefs.current[index];
    if (!panel) return;

    if (isDesktop()) {
      // Swap the visible panel in place; no page scroll on desktop.
      setActiveIndex(index);
      return;
    }

    isClickScrolling.current = true;
    setActiveIndex(index);
    const top = panel.getBoundingClientRect().top + window.scrollY - mobileScrollOffset;
    window.scrollTo({ top, behavior: "smooth" });
    window.setTimeout(() => {
      isClickScrolling.current = false;
    }, 600);
  };

  return (
    <Section id={id} variant="default" background="transparent" className={className}>
      <div className="mb-10 hidden text-center max-w-4xl mx-auto lg:block">
        {sectionLabel && <span className="section-label">{sectionLabel}</span>}
        <Typography variant="h2" as="h2" className={`mb-4 ${titleClassName ?? ""}`}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="body" as="p" className="text-text-secondary max-w-2xl mx-auto">
            {subtitle}
          </Typography>
        ) : null}
      </div>

      {/* Sticky tab strip (mobile): section h2 + tabs + active panel title stay visible while scrolling. */}
      <div className="sticky top-[110px] z-30 -mx-8 px-8 xl:-mx-0 xl:px-0 mb-12 lg:static lg:mx-0 lg:mb-16 lg:z-auto">
        <div
          ref={stickyHeaderRef}
          className="relative bg-white shadow-[0_1px_0_#DDE0E9] before:pointer-events-none before:absolute before:inset-x-0 before:bottom-full before:h-[110px] before:bg-white lg:before:hidden"
        >
          <div className="border-b border-[#DDE0E9] bg-white px-1 pb-3 pt-2 text-center lg:hidden">
            {sectionLabel ? <span className="section-label">{sectionLabel}</span> : null}
            <Typography variant="h2" as="h2" className={`mb-0 text-xl font-bold leading-snug ${titleClassName ?? ""}`}>
              {title}
            </Typography>
          </div>
          <div
            role="tablist"
            aria-label={title}
            className={`flex flex-nowrap gap-1 sm:gap-2 overflow-x-auto overflow-y-hidden scrollbar-hide border-b border-[#DDE0E9] bg-white ${scrollableTabs ? "" : "lg:flex-wrap lg:justify-center lg:overflow-x-hidden"}`}
          >
            {items.map((item, index) => {
              const isActive = index === activeIndex;
              return (
                <button
                  key={item.title}
                  ref={el => {
                    tabRefs.current[index] = el;
                  }}
                  type="button"
                  role="tab"
                  id={`use-case-tab-${index}`}
                  aria-selected={isActive}
                  aria-controls={`use-case-panel-${index}`}
                  onClick={() => goToPanel(index)}
                  className={`shrink-0 px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px outline-none transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${
                    isActive
                      ? "border-accent text-accent"
                      : "border-transparent text-text-secondary hover:text-text-primary"
                  }`}
                >
                  {item.tag ?? item.title}
                </button>
              );
            })}
          </div>
          {/* <p
            className="lg:hidden border-b border-[#DDE0E9] bg-white px-1 py-3 text-lg font-semibold leading-snug text-text-primary"
            aria-live="polite"
          >
            {items[activeIndex]?.title}
          </p> */}
        </div>
      </div>

      <div className="flex flex-col">
        {items.map((item, index) => (
          <div
            key={item.title}
            ref={el => {
              panelRefs.current[index] = el;
            }}
            role="tabpanel"
            id={`use-case-panel-${index}`}
            aria-labelledby={`use-case-tab-${index}`}
            aria-hidden={index !== activeIndex}
            className={`py-12 first:pt-0 last:pb-0 lg:scroll-mt-0 ${
              index === 0 ? "" : "border-t border-[#DDE0E9]"
            } ${index === activeIndex ? "lg:border-t-0 lg:pt-0 lg:pb-0" : "lg:hidden"}`}
            style={{ scrollMarginTop: mobileScrollOffset }}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-14 items-center">
              <div className="min-w-0 order-1">
                <Typography as="h3" variant="h3" className="mb-4 max-lg:sr-only">
                  {item.title}
                </Typography>
                {/* Rendered as a div: some pages pass rich descriptions containing
                    block elements (blockquote, p), which are invalid inside <p>. */}
                <Typography as="div" variant="body" className="text-text-secondary mb-5">
                  {item.description}
                </Typography>
                {item.valuePoint ? (
                  <p className="flex items-start gap-2 border-t border-gray-100 pt-4 mb-5 text-sm text-text-secondary">
                    <span>
                      <span className="font-semibold text-primary">So you can</span>{" "}
                      {item.valuePoint}
                    </span>
                  </p>
                ) : null}
                {item.linkText && item.href ? (
                  <Link
                    href={item.href}
                    className="inline-flex items-center gap-1 text-[13px] font-semibold text-secondary hover:gap-2 transition-all w-fit"
                  >
                    {item.linkText} →
                  </Link>
                ) : null}
              </div>
              <div className="min-w-0 order-2 overflow-hidden rounded-xl border border-[#DDE0E9] bg-slate-100 max-lg:max-h-[min(280px,62vw)] lg:max-h-none">
                <Image
                  src={item.image}
                  alt={item.imageAlt}
                  title={item.imageAlt}
                  width={640}
                  height={480}
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  className={`w-full h-full max-lg:max-h-[min(280px,62vw)] aspect-[4/3] lg:aspect-[4/3] ${
                    item.imageFit === "contain" ? "object-contain" : "object-cover"
                  } ${item.imageClassName ?? ""}`}
                  {...getCdnImageProps(item.image, {
                    unoptimized: false,
                    quality: CDN_IMAGE_QUALITY,
                  })}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {cta ? (
        <div className="mt-12 flex justify-center">
          <Link
            href={cta.href}
            target={cta.target ?? (cta.href.startsWith("http") ? "_blank" : undefined)}
            rel={cta.href.startsWith("http") ? "noreferrer" : undefined}
          >
            <NewButton variant="primary" size="md" arrow="grow">
              {cta.label}
            </NewButton>
          </Link>
        </div>
      ) : null}
    </Section>
  );
};
